// NOTE: useUserAuth() is for end users (consumer). useAuth() (legacy) is for Proloco accounts. Do not merge.
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { posthog } from '../lib/analytics/posthogClient';
import { supabase } from '../lib/supabase';
import { loadGoogleIdentity } from '../lib/googleIdentity';
import type { GooglePromptMomentNotification } from '../types/google-gis';

// ---------- Google Identity Services (GIS) helpers ----------

/** base64url encode a Uint8Array (no padding). */
function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Hex-encode a Uint8Array (lowercase, no separators). */
function hexEncode(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a raw cryptographic nonce (32 random bytes, base64url) and its
 * SHA-256 hash (HEX). The hash is sent to Google (lands in JWT.nonce);
 * the raw value is sent to Supabase, which re-hashes (SHA-256 hex) and compares.
 * IMPORTANT: Supabase GoTrue expects the hash in HEX, not base64url.
 * See: https://supabase.com/docs/guides/auth/social-login/auth-google#using-google-identity-services-gis-button-flow
 */
async function generateNoncePair(): Promise<{ raw: string; hashed: string }> {
  const buf = new Uint8Array(32);
  crypto.getRandomValues(buf);
  const raw = base64UrlEncode(buf);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = hexEncode(new Uint8Array(digest));
  return { raw, hashed };
}

// ---------- Deferred consent flush (T024) ----------

/**
 * localStorage key SignupForm writes to at submit time. Session-less signup
 * (email confirmation pending) means POST /api/consents cannot be called yet —
 * see flushPendingConsents below for why and when it actually fires.
 */
export const PENDING_CONSENTS_KEY = 'buzzmap_pending_consents';

export interface PendingConsentRow {
  kind: 'terms' | 'privacy' | 'marketing';
  granted: boolean;
  documentVersion: string;
}

/**
 * Flush any consent rows staged in localStorage by SignupForm to
 * POST /api/consents, using the just-established session's access token.
 *
 * Called on every SIGNED_IN event (not just the "first" one in a literal
 * sense — there is no reliable way to distinguish first-ever from
 * subsequent sign-ins client-side, and it doesn't matter: the key is only
 * ever written once at signup and removed on success, so a later SIGNED_IN
 * with no pending key is just a no-op). Consent rows are append-only on the
 * server and harmless to duplicate, so a retry from a stale key needs no
 * coordination — it either succeeds and clears the key, or fails (e.g. the
 * token's email still isn't confirmed) and leaves the key for the next
 * SIGNED_IN to retry.
 */
async function flushPendingConsents(accessToken: string): Promise<void> {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(PENDING_CONSENTS_KEY);
  } catch {
    return; // localStorage unavailable — nothing we can do
  }
  if (!raw) return;

  let rows: PendingConsentRow[];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) throw new Error('invalid shape');
    rows = parsed as PendingConsentRow[];
  } catch {
    // Malformed payload can never succeed on retry — drop it rather than
    // getting stuck retrying garbage forever.
    try {
      window.localStorage.removeItem(PENDING_CONSENTS_KEY);
    } catch {
      /* ignore */
    }
    return;
  }

  try {
    const res = await fetch('/api/consents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(rows),
    });
    if (res.ok) {
      try {
        window.localStorage.removeItem(PENDING_CONSENTS_KEY);
      } catch {
        /* ignore */
      }
    }
    // Non-2xx (e.g. email still unconfirmed on this token): leave the key in
    // place so the next SIGNED_IN retries.
  } catch {
    // Network failure: leave the key in place for retry.
  }
}

/**
 * Build a PostHog identify props object containing only defined values.
 * Avoids passing `name: undefined` which is correctly ignored by PostHog
 * but cleaner to omit.
 */
function buildIdentifyProps(user: User): Record<string, string> {
  const props: Record<string, string> = {};
  if (user.email) props.email = user.email;
  const name =
    (user.user_metadata?.full_name as string | undefined) ??
    (user.user_metadata?.name as string | undefined);
  if (name) props.name = name;
  const provider = (user.app_metadata?.provider as string | undefined) ?? 'email';
  props.provider = provider;
  return props;
}

/**
 * UserAuthContext value: end-user authentication state + operations.
 * Backed by Supabase Auth (Google OAuth + email/password).
 */
/** Moderation status of a creator account (from GET /api/creator/me). */
export type CreatorModerationStatus = 'pending' | 'trusted' | 'banned';

/** Verification status of a creator's organization (from GET /api/creator/me). */
export type OrganizationStatus = 'pending' | 'verified' | 'rejected' | 'suspended';

/** Role of the current user within their organization. */
export type OrganizationMemberRole = 'referente' | 'collaboratore';

/**
 * A creator's organization membership, as returned by GET /api/creator/me.
 * `creditBalance` here is the SAME number as the top-level `creditBalance`
 * on the context (the endpoint already resolves the org balance into the
 * top-level field) — it is duplicated on this object only because it is
 * part of the wire shape, not because a consumer should prefer one over
 * the other.
 */
export interface CreatorOrganization {
  id: string;
  name: string;
  status: OrganizationStatus;
  creditBalance: number;
  memberRole: OrganizationMemberRole;
  hasBilling: boolean;
  /**
   * Admin-authored explanation for a 'rejected'/'suspended' status. `null`
   * for every other status, and also `null` when the admin didn't type one —
   * consumers must keep a fixed fallback message for that case.
   */
  rejectionReason: string | null;
}

export interface UserAuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>;
  requestPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Authenticated fetch for the creator console (/api/creator/*).
   * Injects the current Supabase access token as a Bearer header and defaults
   * Content-Type to application/json (unless a FormData body is passed).
   */
  creatorFetch: (url: string, options?: RequestInit) => Promise<Response>;
  /** Creator credit balance (null until the profile is loaded / when signed out). */
  creditBalance: number | null;
  /** Creator moderation status (null until loaded / when signed out). */
  moderationStatus: CreatorModerationStatus | null;
  /** Creator role (null until loaded / when signed out). */
  role: string | null;
  /**
   * Creator's organization membership. `null` while `isProfileLoading` is
   * true (not yet known) AND `null` once loaded when the creator has no
   * organization — check `isProfileLoading` first to tell those two apart.
   */
  organization: CreatorOrganization | null;
  /**
   * True from the moment a user is known until the first GET /api/creator/me
   * for that user settles (success or failure). False when signed out.
   * Exists so callers (T021-FE's dashboard gate) can distinguish
   * "organization not loaded yet" from "loaded and confirmed absent" without
   * racing a redirect against the initial fetch.
   */
  isProfileLoading: boolean;
  /** Re-fetch GET /api/creator/me and refresh creditBalance/moderationStatus/role/organization. */
  refreshCreatorProfile: () => Promise<void>;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);

/**
 * Internal-only: read auth context safely for downstream contexts/hooks that
 * may legitimately render in test environments without a UserAuthProvider.
 * Returns a non-throwing fallback (anonymous, idle) when provider is missing.
 */
export function useUserAuthOptional(): UserAuthContextType {
  const ctx = useContext(UserAuthContext);
  if (ctx) return ctx;
  return {
    user: null,
    session: null,
    isLoading: false,
    signInWithGoogle: async () => {},
    signInWithEmail: async () => {},
    signUpWithEmail: async () => ({ needsConfirmation: false }),
    requestPasswordReset: async () => {},
    signOut: async () => {},
    creatorFetch: async (url: string, options?: RequestInit) => fetch(url, options),
    creditBalance: null,
    moderationStatus: null,
    role: null,
    organization: null,
    isProfileLoading: false,
    refreshCreatorProfile: async () => {},
  };
}

interface UserAuthProviderProps {
  children: ReactNode;
}

/**
 * UserAuthProvider — manages Supabase Auth session lifecycle for end users.
 * Distinct from AuthProvider (Proloco JWT custom). Both providers coexist.
 */
export function UserAuthProvider({ children }: UserAuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [moderationStatus, setModerationStatus] = useState<CreatorModerationStatus | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [organization, setOrganization] = useState<CreatorOrganization | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // Initial session load + subscribe to auth state changes
  useEffect(() => {
    let cancelled = false;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSession(null);
        setUser(null);
        setIsLoading(false);
      });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Handle: INITIAL_SESSION, SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED
      switch (event) {
        case 'SIGNED_IN':
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
          // T016: PostHog identify (auto no-op when opt-out is active)
          if (nextSession?.user) {
            posthog.identify(nextSession.user.id, buildIdentifyProps(nextSession.user));
          }
          // T024: flush any consent choice staged at signup, now that we have
          // a real (and, for email signup, confirmed) session token.
          if (nextSession?.access_token) {
            void flushPendingConsents(nextSession.access_token);
          }
          break;
        case 'INITIAL_SESSION':
        case 'USER_UPDATED':
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
          // T016: PostHog identify (auto no-op when opt-out is active)
          if (nextSession?.user) {
            posthog.identify(nextSession.user.id, buildIdentifyProps(nextSession.user));
          }
          break;
        case 'TOKEN_REFRESHED':
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
          break;
        case 'SIGNED_OUT':
          setSession(null);
          setUser(null);
          // T016: reset distinctId to prevent identity leak across users
          posthog.reset();
          break;
        default:
          // Other events (PASSWORD_RECOVERY, MFA_CHALLENGE_VERIFIED, etc.) — keep state in sync defensively
          setSession(nextSession);
          setUser(nextSession?.user ?? null);
          break;
      }
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  /**
   * Google sign-in via Google Identity Services (GIS) + Supabase signInWithIdToken.
   *
   * Why GIS instead of signInWithOAuth: the OAuth redirect flow shows the
   * Supabase project subdomain (xxx.supabase.co) on the Google consent screen.
   * GIS uses our own Google Cloud client ID, so consent shows buzzeventmap.com.
   *
   * Flow:
   *   1. Generate random nonce + SHA-256 hash.
   *   2. Init GIS with client_id + hashed nonce.
   *   3. Prompt user (One Tap / FedCM popup).
   *   4. Callback receives ID JWT -> pass to Supabase signInWithIdToken with RAW nonce.
   *      Supabase re-hashes the raw nonce and compares with JWT's nonce claim.
   *
   * Limitation: prompt() may be suppressed in incognito or when third-party
   * cookies are blocked. In that case the prompt silently fails; we surface
   * an error after a short timeout asking the user to enable cookies.
   */
  const signInWithGoogle = useCallback(async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      throw new Error('Missing VITE_GOOGLE_CLIENT_ID env var');
    }

    // T205-FE: the GIS script is no longer in index.html; one shared loader injects it on demand.
    const google = await loadGoogleIdentity();
    const { raw, hashed } = await generateNoncePair();

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        fn();
      };

      google.accounts.id.initialize({
        client_id: clientId,
        nonce: hashed,
        auto_select: false,
        use_fedcm_for_prompt: true,
        callback: (response) => {
          if (!response?.credential) {
            finish(() => reject(new Error('Google sign-in returned no credential')));
            return;
          }
          supabase.auth
            .signInWithIdToken({
              provider: 'google',
              token: response.credential,
              nonce: raw,
            })
            .then(({ error }) => {
              if (error) finish(() => reject(error));
              else finish(resolve);
            })
            .catch((err) => finish(() => reject(err)));
        },
      });

      google.accounts.id.prompt((notification: GooglePromptMomentNotification) => {
        // If Google decides not to show the prompt (e.g. third-party cookies
        // blocked, FedCM disabled), surface a user-friendly error so the UI
        // can recover instead of hanging forever.
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          finish(() =>
            reject(
              new Error(
                'Google sign-in non disponibile. Abilita i cookie di terze parti per accedere con Google.'
              )
            )
          );
        }
        // dismissed by user: soft cancel, resolve without error
        if (notification.isDismissedMoment()) {
          finish(resolve);
        }
      });
    });
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUpWithEmail = useCallback(
    async (email: string, password: string): Promise<{ needsConfirmation: boolean }> => {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      // Supabase pattern: when email confirmation is enabled, user is returned but session is null
      const needsConfirmation = Boolean(data.user) && !data.session;
      return { needsConfirmation };
    },
    []
  );

  const requestPasswordReset = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/password-update',
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  /**
   * Authenticated fetch for /api/creator/*. Reads the current access token
   * fresh from Supabase on every call so it survives token refreshes.
   */
  const creatorFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const headers = new Headers(options.headers);
      // Default JSON content-type unless a FormData body is sent (browser sets
      // the multipart boundary itself in that case).
      if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
      }
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return fetch(url, { ...options, headers });
    },
    []
  );

  /**
   * Fetch GET /api/creator/me and hydrate creditBalance/moderationStatus/
   * role/organization. `creditBalance` is taken as-is from the endpoint: the
   * backend already resolves it to the caller's ORGANIZATION balance when
   * one exists, so no separate org-balance path is needed here.
   */
  const refreshCreatorProfile = useCallback(async (): Promise<void> => {
    try {
      const res = await creatorFetch('/api/creator/me');
      if (!res.ok) return;
      const data = await res.json();
      setCreditBalance(typeof data.creditBalance === 'number' ? data.creditBalance : null);
      setModerationStatus((data.moderationStatus as CreatorModerationStatus | undefined) ?? null);
      setRole(typeof data.role === 'string' ? data.role : null);
      const org = data.organization as Record<string, unknown> | null | undefined;
      setOrganization(
        org && typeof org === 'object'
          ? {
              id: String(org.id),
              name: String(org.name),
              status: org.status as OrganizationStatus,
              creditBalance: typeof org.creditBalance === 'number' ? org.creditBalance : 0,
              memberRole: org.memberRole as OrganizationMemberRole,
              hasBilling: Boolean(org.hasBilling),
              rejectionReason:
                typeof org.rejectionReason === 'string' ? org.rejectionReason : null,
            }
          : null
      );
    } catch {
      // Network/parse error — leave existing profile state untouched.
    }
  }, [creatorFetch]);

  // Load the creator profile once per signed-in user; reset when signed out.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) {
      setCreditBalance(null);
      setModerationStatus(null);
      setRole(null);
      setOrganization(null);
      setIsProfileLoading(false);
      return;
    }
    setIsProfileLoading(true);
    void refreshCreatorProfile().finally(() => setIsProfileLoading(false));
  }, [userId, refreshCreatorProfile]);

  const value = useMemo<UserAuthContextType>(
    () => ({
      user,
      session,
      isLoading,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      requestPasswordReset,
      signOut,
      creatorFetch,
      creditBalance,
      moderationStatus,
      role,
      organization,
      isProfileLoading,
      refreshCreatorProfile,
    }),
    [
      user,
      session,
      isLoading,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      requestPasswordReset,
      signOut,
      creatorFetch,
      creditBalance,
      moderationStatus,
      role,
      organization,
      isProfileLoading,
      refreshCreatorProfile,
    ]
  );

  return <UserAuthContext.Provider value={value}>{children}</UserAuthContext.Provider>;
}

/**
 * Hook to access end-user auth context.
 * @throws Error if used outside of UserAuthProvider
 */
export function useUserAuth(): UserAuthContextType {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
}
